import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import { getProtocolKey, getProtocolSource } from 'features/stepByStepProtocol/protocols/config';
import { fetchProtocol } from 'features/stepByStepProtocol/services/protocolService';
import { PROTOCOL_CACHE_TTL_MS, PROTOCOL_STATUS } from 'features/stepByStepProtocol/utils/protocol/constants';
import { ACTIVITY_EXTERNAL_MANUAL_VALUE_APPLIED_ACTION } from 'utils/activityTracking/constants';
import {
    getHostActionTypes,
    getHostFormActions,
    resolveTransactionId,
    selectHostFormField,
    selectHostFormFieldError,
} from 'features/stepByStepProtocol/store/hostSelectors';
import { stepByStepProtocolActions } from 'features/stepByStepProtocol/store/stepByStepProtocolSlice';
import { selectProtocolDefinitionByTransactionId, selectProtocolEntryByKey } from 'features/stepByStepProtocol/store/stepByStepProtocolSelectors';

// AVAILABLE and INVALID depend only on the file's content, so they are reused until the TTL runs
// out. UNAVAILABLE is a read failure and is tried again on the next transaction (no polling).
const CACHEABLE_STATUSES = new Set([PROTOCOL_STATUS.AVAILABLE, PROTOCOL_STATUS.INVALID]);

const isFresh = (entry, now) =>
    !!entry && CACHEABLE_STATUSES.has(entry.status) && entry.loadedAt != null && now - entry.loadedAt < PROTOCOL_CACHE_TTL_MS;

// One breadcrumb per protocol read, so a Sentry event from the panel shows which protocol version (or
// which failure) the Agent had. Never the XML content. Sentry is enabled on staging and production only.
// Never throws: a breadcrumb must not turn a settled read into a failed one.
export function addProtocolLoadBreadcrumb({ protocolKey, status, error = null, meta = null }) {
    try {
        Sentry.addBreadcrumb({
            category: 'stepByStepProtocol',
            message: `Protocol ${protocolKey} ${status}`,
            level: status === PROTOCOL_STATUS.AVAILABLE ? 'info' : 'warning',
            data: {
                protocolKey,
                status,
                error,
                objectKey: meta?.objectKey ?? null,
                lastModified: meta?.lastModified ?? null,
                protocolId: meta?.protocolId ?? null,
                schemaVersion: meta?.schemaVersion ?? null,
            },
        });
    } catch (err) {
        console.warn('stepByStepProtocol: breadcrumb failed', err);
    }
}

export function* ensureProtocolLoaded(protocolKey, inFlightKeys) {
    const entry = yield select(selectProtocolEntryByKey, protocolKey);
    // No yield between this check and `add`, so two transactions arriving together start one read.
    if (inFlightKeys.has(protocolKey) || isFresh(entry, Date.now())) return;
    inFlightKeys.add(protocolKey);

    try {
        if (entry?.status !== PROTOCOL_STATUS.AVAILABLE) yield put(stepByStepProtocolActions.protocolLoadStarted({ protocolKey }));
        const result = yield call(fetchProtocol, protocolKey);
        yield put(stepByStepProtocolActions.protocolLoadSettled({ protocolKey, ...result, loadedAt: Date.now() }));
        yield call(addProtocolLoadBreadcrumb, { protocolKey, ...result });
    } catch (err) {
        const failure = { protocolKey, status: PROTOCOL_STATUS.UNAVAILABLE, error: err?.message || 'Protocol load failed' };
        yield put(stepByStepProtocolActions.protocolLoadSettled({ ...failure, loadedAt: Date.now() }));
        addProtocolLoadBreadcrumb(failure);
    } finally {
        inFlightKeys.delete(protocolKey);
    }
}

export function* onNewTransaction(inFlightKeys, { payload }) {
    try {
        const protocolKey = getProtocolKey(payload?.location_id, payload?.gate_direction);
        const transactionId = resolveTransactionId(payload);
        // `getProtocolKey` builds a key for any location; only a configured one has a protocol.
        if (!getProtocolSource(protocolKey) || transactionId == null) return;

        yield put(stepByStepProtocolActions.attachTransaction({ transactionId, protocolKey }));
        yield call(ensureProtocolLoaded, protocolKey, inFlightKeys);
    } catch (err) {
        console.warn('stepByStepProtocol: new transaction handling failed', err);
    }
}

export function* onCloseTransactionPanel({ payload }) {
    try {
        if (payload?.closeAllPanels) {
            yield put(stepByStepProtocolActions.resetAll());
            return;
        }
        if (payload?.transactionId != null) yield put(stepByStepProtocolActions.detachTransaction(payload.transactionId));
    } catch (err) {
        console.warn('stepByStepProtocol: transaction close handling failed', err);
    }
}

/**
 * Applies a transport type picked in the Detected select exactly like a change of the field in the
 * form: the value write of `changeHandler`, then what `blurHandler` does on leaving the field (touched,
 * inventory validation, and the field validation when the field has one), and the manual activity event
 * a blur would have reported. The panel writes nothing else.
 */
export function* onTransportTypeSelected({ payload }) {
    try {
        const { transactionId, value } = payload ?? {};
        const fieldName = (yield select(selectProtocolDefinitionByTransactionId, transactionId))?.vehicleSwitch?.field;
        if (transactionId == null || !fieldName) return;

        const formActions = getHostFormActions();
        const field = yield select(selectHostFormField, transactionId, fieldName);
        const hadError = yield select(selectHostFormFieldError, transactionId, fieldName);
        const target = { formId: transactionId, fieldName };

        yield put(formActions.setFieldValueAndMarkEdited({ ...target, value }));
        if (hadError) yield put(formActions.setFieldError({ ...target, error: false, helperText: '' }));
        yield put(formActions.setFieldTouched(target));
        yield put(formActions.validateTransactionWithInventoryData({ formId: transactionId }));
        if (field?.validationField) yield put(formActions.validateTransactionFormField(target));
        // The panel select has no focus period on the field, so no blur reports this manual edit (R5-2).
        yield put({ type: ACTIVITY_EXTERNAL_MANUAL_VALUE_APPLIED_ACTION, payload: target });
    } catch (err) {
        console.warn('stepByStepProtocol: transport type change failed', err);
    }
}

// Every worker settles inside its own try/catch: `rootSaga` runs once with no restart, so a throw
// here would take every other saga down with it.
export function* stepByStepProtocolSaga() {
    const inFlightKeys = new Set();
    const hostActionTypes = getHostActionTypes();
    yield all([
        takeEvery(hostActionTypes.newTransaction, onNewTransaction, inFlightKeys),
        takeEvery(hostActionTypes.closeTransactionPanel, onCloseTransactionPanel),
        takeEvery(stepByStepProtocolActions.transportTypeSelected.type, onTransportTypeSelected),
    ]);
}
